import { Component, DoCheck, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Question } from '@app/core/models/questionnaire.model';
import { QuestionSubType } from '@app/core/enums/questionnaire.enums';

@Component({
  selector: 'app-rank-order',
  imports: [CommonModule],
  templateUrl: './rank-order.html',
  styleUrl: './rank-order.scss'
})
export class RankOrder implements OnChanges, DoCheck {
  @Input() data: any | null = null;
  @Input() question: Question | null = null;
  @Input() viewMode: 'mobile' | 'desktop' = 'desktop';
  @Output() rankingChanged = new EventEmitter<any[]>();
  
  questionSubType = QuestionSubType;
  rankedItems: any[] = [];
  draggedItem: any = null;
  draggedIndex: number = -1;
  private lastQuestionSignature = '';

  ngOnInit() {
    this.refreshRankedItems();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question']) {
      this.refreshRankedItems();
    }
  }

  ngDoCheck(): void {
    const currentSignature = this.getQuestionSignature();
    if (currentSignature !== this.lastQuestionSignature) {
      this.refreshRankedItems();
    }
  }

  onDragStart(event: DragEvent, item: any, index: number) {
    this.draggedItem = item;
    this.draggedIndex = index;
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/html', '');
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  }

  onDragEnter(event: DragEvent, index: number) {
    event.preventDefault();
  }

  onDrop(event: DragEvent, dropIndex: number) {
    event.preventDefault();
    
    if (this.draggedIndex !== dropIndex && this.draggedItem) {
      // Remove the dragged item from its original position
      const draggedItem = this.rankedItems.splice(this.draggedIndex, 1)[0];
      
      // Insert it at the new position
      this.rankedItems.splice(dropIndex, 0, draggedItem);
      
      // Update ranks
      this.updateRanks();
      
      // Emit the change
      this.rankingChanged.emit(this.rankedItems);
    }
    
    this.draggedItem = null;
    this.draggedIndex = -1;
  }

  onDragEnd(event: DragEvent) {
    this.draggedItem = null;
    this.draggedIndex = -1;
  }

  moveItemUp(index: number) {
    if (index > 0) {
      const item = this.rankedItems.splice(index, 1)[0];
      this.rankedItems.splice(index - 1, 0, item);
      this.updateRanks();
      this.rankingChanged.emit(this.rankedItems);
    }
  }

  moveItemDown(index: number) {
    if (index < this.rankedItems.length - 1) {
      const item = this.rankedItems.splice(index, 1)[0];
      this.rankedItems.splice(index + 1, 0, item);
      this.updateRanks();
      this.rankingChanged.emit(this.rankedItems);
    }
  }

  private updateRanks() {
    this.rankedItems.forEach((item, index) => {
      item.currentRank = index + 1;
    });
  }

  private refreshRankedItems(): void {
    if (!this.question?.questionOptions?.length) {
      this.rankedItems = [];
      this.lastQuestionSignature = this.getQuestionSignature();
      return;
    }

    this.rankedItems = [...this.question.questionOptions].map((item, index) => ({
      ...item,
      currentRank: index + 1,
    }));
    this.lastQuestionSignature = this.getQuestionSignature();
  }

  private getQuestionSignature(): string {
    const questionId = (this.question as any)?.questionId ?? '';
    const subtype = (this.question as any)?.questionSubType ?? '';
    const options = this.question?.questionOptions ?? [];
    const optionSignature = options
      .map((opt: any) => `${opt.optionId ?? ''}|${opt.order ?? ''}|${opt.text ?? ''}`)
      .join('||');
    return `${questionId}::${subtype}::${optionSignature}`;
  }

  // getRankIcon(rank: number): string {
  //   switch (rank) {
  //     case 1: return '🥇';
  //     case 2: return '🥈';
  //     case 3: return '🥉';
  //     default: return `${rank}`;
  //   }
  // }

  // getRankClass(rank: number): string {
  //   if (rank <= 3) return 'top-rank';
  //   return 'regular-rank';
  // }
}
